// The south row in motion, layered into the Square's ambient passes like
// the Reach's (ambient.js calls each of these): smoke rising from the volcano
// and its crater glowing after dark, spray drifting off the falls, dolphins
// leaping in the warm sea, parrots crossing the Wildwood by day and fireflies
// in it at night. Culled to the view; still under reduced motion.
import { rng } from "./paint"

const TAU = Math.PI * 2

export class SouthAmbient {
    constructor(world, { seed = 1, reduceMotion = false, glow } = {}) {
        this.world = world
        this.reduce = reduceMotion
        this.glow = glow
        this.r = rng(seed + 950)
        this.t = 0
        this.view = { x: 0, y: 0, w: world.W, h: world.H }
        this.smoke = []
        this.spray = []
        const isles = world.isles, wildwood = world.wildwood
        this.volcanoes = isles?.volcanoes || []
        this.falls = wildwood?.falls || null
        this.active = !!(isles?.active || wildwood?.active)
        if (!this.active) return
        const r = this.r
        // dolphins: a pod that surfaces now and then in the warm sea
        this.pod = null
        this.nextPod = 3 + r() * 6
        // parrots and fireflies over the Wildwood
        this.parrots = null
        this.nextParrots = 4 + r() * 8
        const forest = []
        for (let i = 0; i < 600 && forest.length < 80; i++) {
            const x = 8200 + r() * 3700, y = 5300 + r() * 4600
            if (wildwood.inForest(x, y) && !world.blocked(x, y)) forest.push({ hx: x, hy: y, x, y, phase: r() * TAU, speed: 0.6 + r() * 0.8, blink: 1.5 + r() * 2 })
        }
        this.fireflies = forest
        // a few small sailboats on slow loops through the open water, clear of
        // the islands and the viaduct (where they are is the clock's business)
        this.sails = [
            { x: 2100, y: 6450, rx: 700, ry: 200, period: 190, phase: 0, color: 0 },
            { x: 2000, y: 8750, rx: 560, ry: 170, period: 160, phase: 0.4, color: 1 },
            { x: 1500, y: 5650, rx: 650, ry: 130, period: 210, phase: 0.7, color: 2 }
        ].filter(() => isles?.active)
    }

    setView(view) { this.view = view }
    #visible(x, y, pad = 120) {
        const v = this.view
        return x > v.x - pad && x < v.x + v.w + pad && y > v.y - pad && y < v.y + v.h + pad
    }

    update(dt, { day, drift }) {
        if (!this.active) return
        this.t += Math.max(0, dt)
        if (this.reduce) return
        const r = this.r
        const advance = (list, fn) => { for (let i = list.length - 1; i >= 0; i--) { const p = list[i]; p.age += dt; if (p.age >= p.life) list.splice(i, 1); else fn(p) } }
        this.volcanoes.forEach((v) => {
            const top = { x: v.x, y: v.y - v.s * 0.82 }
            if (this.#visible(top.x, top.y, 900) && r() < dt * 2.2) this.smoke.push({ x: top.x + (r() - 0.5) * v.s * 0.4, y: top.y, vx: drift * 0.4 + (r() - 0.5) * 8, vy: -18 - r() * 12, age: 0, life: 6 + r() * 3, s: 10 + r() * 8 })
        })
        advance(this.smoke, (p) => { p.x += (p.vx + drift * 0.8 * (p.age / p.life)) * dt; p.y += p.vy * dt; p.vy *= 0.996 })
        const f = this.falls
        if (f && f.pool && this.#visible(f.pool.x, f.pool.y, 500) && r() < dt * 7) this.spray.push({ x: f.x + (r() - 0.5) * 60, y: f.pool.y - f.pool.ry * 0.5 + r() * 10, vx: (r() - 0.5) * 20, vy: -10 - r() * 14, age: 0, life: 1.6 + r(), s: 4 + r() * 6 })
        advance(this.spray, (p) => { p.x += p.vx * dt; p.y += p.vy * dt })
        // a pod of dolphins arcs out of the warm sea
        this.nextPod -= dt
        if (!this.pod && this.nextPod <= 0) {
            for (let k = 0; k < 20; k++) {
                const x = 300 + r() * 3600, y = 5600 + r() * 4200
                if (!this.world.inSea(x, y, 0) || this.world.islandAt(x, y, -300)) continue
                this.pod = { x, y, dir: r() < 0.5 ? -1 : 1, age: 0, life: 7, members: [0, 1, 2].map((i) => ({ dx: i * 34, dy: (i % 2 ? 16 : -10), phase: i * 0.55 })) }
                break
            }
            this.nextPod = 10 + r() * 14
        }
        if (this.pod) { this.pod.age += dt; this.pod.x += this.pod.dir * 60 * dt; if (this.pod.age >= this.pod.life) this.pod = null }
        // parrots cross the canopy by day
        this.nextParrots -= dt
        if (!this.parrots && this.nextParrots <= 0 && day) {
            const fromLeft = r() < 0.5, y = 5600 + r() * 4000
            this.parrots = { x: fromLeft ? 7800 : 12200, y, vx: (fromLeft ? 1 : -1) * (170 + r() * 50), vy: (r() - 0.5) * 30, members: Array.from({ length: 3 + Math.floor(r() * 3) }, (_, i) => ({ dx: -i * 26 * (fromLeft ? 1 : -1), dy: (i % 2 ? 1 : -1) * i * 8, phase: r() * TAU, color: ["#E8543A", "#3E8FB0", "#F2C14E", "#5FB35A"][i % 4] })) }
            this.nextParrots = 18 + r() * 20
        }
        if (this.parrots) { this.parrots.x += this.parrots.vx * dt; this.parrots.y += this.parrots.vy * dt; if (this.parrots.x < 7600 || this.parrots.x > 12400) this.parrots = null }
        this.fireflies.forEach((ff) => {
            ff.phase += dt * ff.speed
            ff.x = ff.hx + Math.sin(ff.phase * 0.9) * 26 + Math.sin(ff.phase * 2.1) * 8
            ff.y = ff.hy + Math.cos(ff.phase * 0.7) * 18 + Math.sin(ff.phase * 1.7) * 6
        })
    }

    drawWater(ctx, pal) {
        if (!this.active) return
        this.#drawSails(ctx, pal)
        if (!this.pod || !this.#visible(this.pod.x, this.pod.y, 200)) return
        const p = this.pod, k = p.age / p.life
        ctx.save()
        p.members.forEach((m) => {
            const leap = Math.sin(((this.t * 1.4 + m.phase) % 1) * Math.PI)
            const x = p.x + m.dx * p.dir, y = p.y + m.dy
            ctx.strokeStyle = pal.foam; ctx.globalAlpha = 0.7 * (1 - k * 0.5); ctx.lineWidth = 1.4
            ctx.beginPath(); ctx.ellipse(x, y + 2, 14, 4, 0, 0, TAU); ctx.stroke()
            ctx.globalAlpha = 1
            ctx.save(); ctx.translate(x, y - leap * 16); ctx.scale(p.dir, 1); ctx.rotate(-0.5 + leap)
            ctx.fillStyle = pal.night ? "#4E5E6A" : "#7A8E9E"
            ctx.beginPath(); ctx.ellipse(0, 0, 13, 4.4, 0, 0, TAU); ctx.fill()
            ctx.strokeStyle = pal.ink; ctx.lineWidth = 1; ctx.stroke()
            ctx.beginPath(); ctx.moveTo(-2, -3); ctx.lineTo(2, -9); ctx.lineTo(5, -3); ctx.fill(); ctx.stroke()
            ctx.restore()
        })
        ctx.restore()
    }

    // A sailboat: a little hull with a wake, and a sail leaning with the
    // turn. Positions come from `t` alone, so reduced motion holds them still.
    #drawSails(ctx, pal) {
        const colors = [pal.cloth, pal.flag[0], pal.flag[2]]
        this.sails.forEach((b) => {
            const a = (((this.reduce ? 0 : this.t) / b.period + b.phase) % 1) * TAU
            const x = b.x + Math.cos(a) * b.rx, y = b.y + Math.sin(a) * b.ry
            if (!this.#visible(x, y, 80)) return
            const dir = -Math.sin(a) >= 0 ? 1 : -1 // heading east or west along the loop
            ctx.save()
            ctx.translate(x, y)
            ctx.strokeStyle = pal.foam; ctx.globalAlpha = 0.55; ctx.lineWidth = 1.4
            ctx.beginPath(); ctx.moveTo(-dir * 16, 3); ctx.quadraticCurveTo(-dir * 34, 5, -dir * 52, 1); ctx.moveTo(-dir * 16, 7); ctx.quadraticCurveTo(-dir * 32, 10, -dir * 48, 12); ctx.stroke()
            ctx.globalAlpha = 1
            ctx.scale(dir, 1)
            ctx.beginPath(); ctx.moveTo(-18, 0); ctx.lineTo(20, 0); ctx.quadraticCurveTo(14, 9, -12, 8); ctx.closePath()
            ctx.fillStyle = pal.woodDark; ctx.fill(); ctx.strokeStyle = pal.ink; ctx.lineWidth = 1.3; ctx.stroke()
            ctx.strokeStyle = pal.ink; ctx.lineWidth = 1.6; ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(0, -40); ctx.stroke()
            ctx.beginPath(); ctx.moveTo(1, -38); ctx.quadraticCurveTo(16, -18, 16, -3); ctx.lineTo(1, -3); ctx.closePath()
            ctx.fillStyle = colors[b.color % colors.length]; ctx.fill(); ctx.strokeStyle = pal.ink; ctx.lineWidth = 1.2; ctx.stroke()
            ctx.beginPath(); ctx.moveTo(-1, -34); ctx.lineTo(-13, -4); ctx.lineTo(-1, -4); ctx.closePath()
            ctx.fillStyle = pal.cloth; ctx.fill(); ctx.stroke()
            ctx.restore()
        })
    }

    drawAir(ctx, pal, light) {
        if (!this.active) return
        ctx.save()
        this.smoke.forEach((p) => {
            const k = p.age / p.life
            ctx.globalAlpha = Math.sin(k * Math.PI) * (pal.night ? 0.3 : 0.4)
            ctx.fillStyle = pal.night ? "#6E7478" : "#B8B4AE"
            ctx.beginPath(); ctx.arc(p.x, p.y, p.s + k * 40, 0, TAU); ctx.fill()
        })
        this.spray.forEach((p) => {
            const k = p.age / p.life
            ctx.globalAlpha = Math.sin(k * Math.PI) * 0.55
            ctx.fillStyle = "#FFFFFF"
            ctx.beginPath(); ctx.arc(p.x, p.y, p.s + k * 10, 0, TAU); ctx.fill()
        })
        ctx.globalAlpha = 1
        if (this.parrots && light.day) {
            const flock = this.parrots
            flock.members.forEach((m) => {
                const x = flock.x + m.dx, y = flock.y + m.dy
                ctx.fillStyle = `rgba(${pal.shadow},0.14)`
                ctx.beginPath(); ctx.ellipse(x + 50, y + 90, 7, 2.5, 0, 0, TAU); ctx.fill()
                const flap = Math.sin(this.t * 12 + m.phase) * 5
                ctx.strokeStyle = m.color; ctx.lineWidth = 2.6; ctx.lineCap = "round"
                ctx.beginPath(); ctx.moveTo(x - 8, y - flap); ctx.quadraticCurveTo(x - 3, y - 3, x, y); ctx.quadraticCurveTo(x + 3, y - 3, x + 8, y - flap); ctx.stroke()
            })
        }
        ctx.restore()
    }

    drawLights(ctx, pal, light) {
        if (!this.active || !this.glow) return
        ctx.save()
        ctx.globalCompositeOperation = "lighter"
        const t = this.reduce ? 0 : this.t
        // the crater glows, and more so after dark
        this.volcanoes.forEach((v) => {
            const x = v.x, y = v.y - v.s * 0.8
            if (!this.#visible(x, y, 400)) return
            ctx.globalAlpha = (light.lamps > 0.5 ? 0.9 : 0.35) * (0.8 + 0.2 * Math.sin(t * 1.7))
            ctx.drawImage(this.glow, x - v.s * 0.5, y - v.s * 0.3, v.s, v.s * 0.6)
        })
        if (light.lamps > 0.5) {
            this.fireflies.forEach((f) => {
                if (!this.#visible(f.x, f.y)) return
                const b = Math.max(0, Math.sin(t * f.blink + f.phase * 3))
                if (b < 0.05) return
                ctx.globalAlpha = b * 0.9 * light.lamps
                ctx.drawImage(this.glow, f.x - 7, f.y - 7, 14, 14)
            })
        }
        ctx.restore()
    }
}
